/**
 * @file src/input/midi/midiSliceSource.js
 * @description Where a MIDI slice comes from — the server when it answers, this browser when it
 * does not.
 *
 * A "slice" is one entity's bindings as the backend stores them: `{ [parameterId]: { cc, channel } }`
 * under `orbiter:<id>` or `collection:<id>`. It is the unit the server returns, the unit
 * `ScopedMidiMap` replaces, and therefore the unit that is mirrored — one row per slice, so learning
 * a CC rewrites that slice and nothing else.
 *
 * ## Why this exists rather than a second store
 *
 * `ScopedMidiMap` already owns loaded-vs-empty, per-slice replacement and write epochs. Adding a
 * second runtime map for the offline copy would mean two answers to "what is mapped". So this feeds
 * the seam that is already there: it resolves slices, and the caller hands them to `loadSlice`
 * exactly as it hands the server's.
 *
 * ## The scope preference rides in its collection's slice
 *
 * `collection-midi-scope` is stored as a parameter inside the collection's own slice, because that
 * is where the backend keeps it. It was FETCHED separately, so the same snapshot arrived twice per
 * boot with no shared cache between the two callers. Both now come through here, which is one
 * request, one row, and one answer that cannot disagree with itself.
 */
import {
  readMidiMappings,
  saveMidiMapping,
  clearMidiMappingRemote,
  MIDI_SCOPE_PREFERENCE_PARAM_ID,
} from '../../api/midiLearnService.js';
import {
  CONFIGURATION_KINDS,
  READ_OUTCOME,
  VALUE_SOURCE,
  beginConfigurationOperation,
  isConfigurationEpochCurrent,
  forgetResolvedKind,
  mirrorAccepted,
  peekResolvedConfiguration,
  readHeldConfigurationValue,
  readThrough,
} from '../../config/configurationPack.js';
import { makeScopeKey, parseScopeKey } from './scopeKey.js';

/** The tree key a scope's entities are listed under in the response (`orbiter` → `orbiters`). */
function treeBranch(scope) {
  return `${scope}s`;
}

/** One entity's slice out of a whole-tree or scoped response. */
function sliceFromTree(payload, scope, entityId) {
  const branch = payload?.midiLearn?.[treeBranch(scope)];
  const slice = branch && typeof branch === 'object' ? branch[entityId] : null;
  return slice && typeof slice === 'object' ? slice : null;
}

/**
 * A tree response that carries the branch but not this entity is the server saying this entity has
 * nothing — an authoritative absence, and the only thing here that clears a mapping.
 *
 * A response missing the branch has not answered about this entity AT ALL: a scoped read of a
 * collection that comes back carrying only `orbiters`, or a `midiLearn` with no branches from a
 * half-deployed backend, says nothing about the entity that was asked for. Read as an absence —
 * which it was — either one deleted the mirrored slice, so one odd response cost somebody every
 * mapping they had. It is a shape we did not expect, and that is what `invalid-response` is for:
 * serve what is on disk, change nothing.
 */
function outcomeForEntity(payload, scope, entityId, treeOutcome) {
  if (treeOutcome !== READ_OUTCOME.NETWORK) return treeOutcome;
  if (!payload?.midiLearn || typeof payload.midiLearn !== 'object') {
    return READ_OUTCOME.INVALID_RESPONSE;
  }
  const branch = payload.midiLearn[treeBranch(scope)];
  if (!branch || typeof branch !== 'object') return READ_OUTCOME.INVALID_RESPONSE;
  const slice = branch[entityId];
  if (!slice || typeof slice !== 'object') return READ_OUTCOME.ABSENT;
  return READ_OUTCOME.NETWORK;
}

/**
 * Reads in flight, one entry per slice they will answer for.
 *
 * The pack's own memo dedupes reads that have SETTLED; this dedupes the ones that have not, which is
 * the half the scope preference exercises: the studio asks for a collection's mappings and its scope
 * preference in the same tick, from two modules that know nothing about each other, and without this
 * each starts its own request for the same slice.
 *
 * An entry stands for the WHOLE read — the request, and the pack recording what it found — not for
 * the network part alone. Dropped when the request settled, it left a window in which the answer
 * existed but nothing had been written down yet; a caller arriving inside that window found no memo
 * and no request in flight, and started a second one.
 * @type {Map<string, Promise<{value: any, source: string, outcome: string, observedAt: number}>>}
 */
const pendingSliceFetches = new Map();

/**
 * Fetch the slices this page does not already hold, in as few requests as the backend allows.
 *
 * One orbiter present → the scoped request the hot path has always used. Several → one whole-tree
 * pull, split per orbiter. Every other scope is a scoped request of its own, which in practice is
 * the one collection a studio shows.
 * @param {Array<{key: string, scope: string, entityId: string}>} wanted
 * @returns {Promise<Map<string, {outcome: string, value: object|null}>>}
 */
async function fetchSlices(wanted) {
  const answers = new Map();
  const orbiters = wanted.filter((entry) => entry.scope === 'orbiter');
  const others = wanted.filter((entry) => entry.scope !== 'orbiter');
  const requests = [];

  if (orbiters.length) {
    const scopeRef = orbiters.length === 1 ? orbiters[0].entityId : undefined;
    requests.push(
      readMidiMappings(scopeRef).then(({ outcome, value }) => {
        for (const { key, scope, entityId } of orbiters) {
          answers.set(key, {
            outcome: outcomeForEntity(value, scope, entityId, outcome),
            value: sliceFromTree(value, scope, entityId),
          });
        }
      }),
    );
  }

  for (const { key, scope, entityId } of others) {
    requests.push(
      readMidiMappings({ scope, entityId }).then(({ outcome, value }) => {
        answers.set(key, {
          outcome: outcomeForEntity(value, scope, entityId, outcome),
          value: sliceFromTree(value, scope, entityId),
        });
      }),
    );
  }

  // Slices resolve independently: one scope the backend does not know must not stop the others.
  await Promise.allSettled(requests);
  return answers;
}

/**
 * The bindings for each requested slice, from wherever they can honestly be had.
 *
 * @param {string[]} scopeKeys slices currently live (see scopeKey.js)
 * @returns {Promise<Map<string, {slice: object|null, source: string, outcome: string, observedAt: number}>>}
 */
export async function loadMidiSlices(scopeKeys) {
  const parsed = (scopeKeys || [])
    .map((key) => ({ key, ...(parseScopeKey(key) || {}) }))
    .filter((entry) => entry.scope && entry.entityId);
  if (!parsed.length) return new Map();

  // Whose mappings these are has to be settled BEFORE a request goes out, and this is the last
  // point before one does. `MIDIControllerInstance` is built at module load and starts loading in
  // its constructor — before any boot has established an identity — so without this, a slice read
  // on a shared browser resolves against whoever was here last: the mirror is read under the
  // previous person's owner key, and a fresh server answer is WRITTEN under it. Asking afterwards
  // is not the same question: it answers with whoever arrived DURING the request. The owner gate
  // this waits on has its own ceiling, so a reconcile that never settles delays this by seconds
  // rather than forever.
  const identity = await beginConfigurationOperation();
  // Somebody else arrived while that was settling. This load belongs to the person who left, and
  // the requests it is about to register would be handed to the arriving one as if they were theirs
  // — who would then wait on a read whose answer nothing is allowed to apply. The identity edge
  // starts a load of its own; this one is simply over.
  if (!isConfigurationEpochCurrent(identity.epoch)) {
    return new Map(
      parsed.map((entry) => [
        entry.key,
        { slice: null, source: VALUE_SOURCE.NONE, outcome: READ_OUTCOME.ABORTED, observedAt: 0 },
      ]),
    );
  }

  // Only the slices this page has neither resolved nor already asked for cost a request. Two
  // callers wanting the same collection — the mappings and the scope preference, in the same tick
  // — is the case this exists for.
  const wanted = parsed.filter(
    (entry) => !peekResolvedConfiguration(CONFIGURATION_KINDS.MIDI, entry.key)
      && !pendingSliceFetches.has(entry.key),
  );
  if (wanted.length) {
    const batch = fetchSlices(wanted);
    for (const entry of wanted) {
      // Dropped when it settles — but only if it is still OURS. A read abandoned by a change of
      // identity settles after the arriving person has registered a read of their own for the same
      // slice, and taking that one out of the map would leave the next caller starting a third.
      const read = readThrough({
        kind: CONFIGURATION_KINDS.MIDI,
        resourceKey: entry.key,
        identity,
        read: async () => (await batch).get(entry.key) || { outcome: READ_OUTCOME.UNAVAILABLE, value: null },
      }).finally(() => {
        if (pendingSliceFetches.get(entry.key) === read) pendingSliceFetches.delete(entry.key);
      });
      pendingSliceFetches.set(entry.key, read);
    }
  }

  const resolvedSlices = new Map();
  await Promise.all(
    parsed.map(async (entry) => {
      // Either this page is already reading this slice — the entry just registered above, or one an
      // earlier caller left — or it has resolved it, and the pack answers from its memo without
      // asking anybody.
      const pending = pendingSliceFetches.get(entry.key);
      const answer = pending
        ? await pending
        : await readThrough({
            kind: CONFIGURATION_KINDS.MIDI,
            resourceKey: entry.key,
            identity,
            read: async () => ({ outcome: READ_OUTCOME.UNAVAILABLE, value: null }),
          });
      resolvedSlices.set(entry.key, {
        slice: answer.value,
        source: answer.source,
        outcome: answer.outcome,
        observedAt: answer.observedAt,
      });
    }),
  );
  return resolvedSlices;
}

/**
 * Keep the mirror in step with a binding the SERVER HAS ACCEPTED.
 *
 * Called after the PUT resolves, never before it: this is not an offline queue, it is the local
 * copy catching up with a change that already happened. Without it the mirror is stale from the
 * first mapping anybody learns — which is exactly the mapping they will go looking for offline.
 *
 * A slice is mirrored WHOLE, so this one binding has to be put back into the rest of it. Where the
 * rest comes from matters: the page memo holds it only for a slice this page actually resolved, and
 * a learn on a slice it never did — a widget mapped before its own load landed, anything mapped
 * after the memo was dropped — would otherwise start from `{}` and put an empty slice with one
 * binding in it over a row holding every other mapping the person had.
 *
 * @param {string} scopeKey the slice that changed
 * @param {string} parameterId the parameter, stored-qualified exactly as it goes on the wire
 * @param {{deviceId?: string, channel: number, cc: number}|null} binding null to forget it
 * @param {{epoch: number, ownerKey: string|null}} identity who the accepted write was for
 */
async function mirrorMidiBinding(scopeKey, parameterId, binding, identity) {
  if (!scopeKey || !parameterId) return false;
  const held = await readHeldConfigurationValue(CONFIGURATION_KINDS.MIDI, scopeKey);
  const slice = held && typeof held === 'object' ? { ...held } : {};
  if (binding) slice[parameterId] = binding;
  else delete slice[parameterId];
  return mirrorAccepted({
    kind: CONFIGURATION_KINDS.MIDI,
    resourceKey: scopeKey,
    value: slice,
    identity,
  });
}

/**
 * Save one binding and record it locally once the server has taken it.
 *
 * The mirror write is awaited rather than fired off, so a caller that resolves is a caller whose
 * change is durable in both places — but a mirror that cannot be written is not a save that failed,
 * and does not become one.
 *
 * Whose binding this is, is settled BEFORE the PUT. The mapping belongs to the person who learned
 * it however long the round trip takes; captured afterwards it would belong to whoever the host has
 * swapped to by the time the server answers, and one person's binding would be written into
 * another's mirror.
 * @param {{scope: string, entityId: string, parameterId: string, binding: object}} payload
 */
export async function saveMidiBinding({ scope, entityId, parameterId, binding }) {
  const identity = await beginConfigurationOperation();
  // Settling who it is for can itself straddle the switch — the owner gate waits on a reconcile,
  // and somebody arriving inside that wait leaves this gesture belonging to the person who has
  // gone. Sent anyway, it goes out on the ARRIVING session: the previous person's mapping written
  // into this one's account ON THE SERVER, where no local guard can take it back. So the epoch is
  // checked in both places — here before anything is issued, and again in `mirrorAccepted` before
  // anything is kept.
  if (!isConfigurationEpochCurrent(identity.epoch)) return null;
  const result = await saveMidiMapping({ scope, entityId, parameterId, binding });
  await mirrorMidiBinding(makeScopeKey(scope, entityId), parameterId, binding, identity).catch(() => {});
  return result;
}

/** Remove one binding, and forget it locally once the server has. Same identity rule as the save,
 *  checked at both edges: a clear issued as the person who left would delete a mapping out of the
 *  arriving person's account. */
export async function clearMidiBinding({ scope, entityId, parameterId }) {
  const identity = await beginConfigurationOperation();
  if (!isConfigurationEpochCurrent(identity.epoch)) return null;
  const result = await clearMidiMappingRemote({ scope, entityId, parameterId });
  await mirrorMidiBinding(makeScopeKey(scope, entityId), parameterId, null, identity).catch(() => {});
  return result;
}

/**
 * A collection's saved MIDI-scope preference, from the same slice its mappings come from.
 *
 * Returns `'collection'`, `'orbiter'`, or null when the collection has none saved and nothing can
 * be inferred. A slice that could not be had at all also answers null — the caller falls back to
 * the default rather than blocking a boot on it, which is what it did before.
 *
 * @param {string} collectionId
 * @returns {Promise<'collection'|'orbiter'|null>}
 */
export async function readMidiScopePreference(collectionId) {
  if (!collectionId) return null;
  const scopeKey = `collection:${collectionId}`;
  const resolvedSlices = await loadMidiSlices([scopeKey]);
  const slice = resolvedSlices.get(scopeKey)?.slice;
  if (!slice || typeof slice !== 'object') return null;

  const value = slice[MIDI_SCOPE_PREFERENCE_PARAM_ID]?.value;
  if (value === 'orbiter' || value === 'collection') return value;

  // No preference saved, but the collection already HAS stage mappings — infer `collection` rather
  // than reading as "never chose". Covers a preference write that failed after the user had already
  // mapped: without this they would come back to a surface that looks unmapped.
  return Object.keys(slice).some((key) => key.startsWith('slot-')) ? 'collection' : null;
}

/**
 * Persist a collection's MIDI-scope preference — one parameter inside that collection's own slice,
 * mirrored with everything else in it.
 * @param {string} collectionId
 * @param {'collection'|'orbiter'} value
 */
export async function saveMidiScopePreference(collectionId, value) {
  if (!collectionId) return null;
  return saveMidiBinding({
    scope: 'collection',
    entityId: collectionId,
    parameterId: MIDI_SCOPE_PREFERENCE_PARAM_ID,
    binding: { value },
  });
}

/**
 * Ask again for every slice, next time one is wanted.
 *
 * The page's memo is what keeps two callers wanting the same slice down to one request, and every
 * local learn or unmap updates it — so it is right about anything that happened HERE. It cannot be
 * right about a mapping changed on another device, and this is how a surface that has reason to
 * believe that happened says so.
 */
export function forgetMidiSlices() {
  forgetResolvedKind(CONFIGURATION_KINDS.MIDI);
}

/**
 * Who to tell when the person here changes.
 *
 * The MIDI runtime holds the previous person's bindings in memory, and it cannot be reached from
 * `sessionAuth`: importing it there would pull the whole MIDI stack — and everything it reaches, up
 * to the voice registry and the scene — into the auth module graph for the sake of one call. So the
 * runtime registers here instead, where the slices it holds come from.
 * @type {Set<() => void>}
 */
const identityListeners = new Set();

/**
 * Be told when the slices this page holds stop belonging to the person here.
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export function onMidiIdentityChange(listener) {
  if (typeof listener !== 'function') return () => {};
  identityListeners.add(listener);
  return () => identityListeners.delete(listener);
}

/**
 * Forget the requests this page has in flight, and say so.
 *
 * Called at the identity edges beside `resetConfigurationPack`. The answers themselves cannot be
 * misfiled — every write is refused once the identity it started for is gone — but a caller
 * arriving after the switch would otherwise be handed the previous person's in-flight request as if
 * it were its own, and the runtime would go on driving the orbiter from bindings that person
 * learned.
 *
 * What it tells goes and asks again for whoever is here now, which is why `sessionAuth` calls this
 * only once the owner reconcile for the arriving person is armed.
 */
export function resetMidiSliceSource() {
  pendingSliceFetches.clear();
  identityListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One surface failing to drop its bindings must not stop the others from dropping theirs.
    }
  });
}


/**
 * Replace one slice's MIDI mappings with another slice's of the SAME scope (the
 * load-saved-mappings dialog: orbiter → orbiter parameter bindings, collection → collection
 * shell bindings). Copy-first-then-clear so a mid-flight failure never leaves the target
 * empty: every source binding is written before any leftover target binding is cleared. Runs
 * sequentially because legacy-scope PUTs are read-modify-writes of the same user-settings
 * document — parallel writes could clobber each other. (Collection-scope writes are atomic
 * per parameter server-side, but the sequential shape keeps one code path and a determinate
 * progress bar.)
 *
 * @param {object} args
 * @param {string} args.scope - the slice scope ('orbiter' | 'collection').
 * @param {Record<string, object>} args.sourceBindings - parameterId → binding, from the source slice.
 * @param {string} args.targetEntityId - the entity being overwritten (the current one).
 * @param {string[]} [args.targetParamIds] - parameterIds the target currently has, to clear leftovers.
 * @param {(done: number, total: number) => void} [args.onProgress] - called after each step (and once
 *   up front with done=0) so the UI can show a determinate loading bar across the sequential writes.
 */
export async function replaceScopedMappings({ scope, sourceBindings, targetEntityId, targetParamIds = [], onProgress }) {
  if (!scope || !targetEntityId) {
    throw new Error('replaceScopedMappings requires a scope and a targetEntityId.');
  }
  const source = sourceBindings && typeof sourceBindings === 'object' ? sourceBindings : {};
  const srcParamIds = Object.keys(source);

  // Leftovers = target params the source won't overwrite (cleared after the copy phase).
  const srcSet = new Set(srcParamIds);
  const leftovers = targetParamIds.filter((parameterId) => !srcSet.has(parameterId));

  const total = srcParamIds.length + leftovers.length;
  let done = 0;
  const report = typeof onProgress === 'function' ? onProgress : () => {};
  report(0, total);
  const step = () => report((done += 1), total);

  // Copy phase — write every source binding into the target first.
  for (const parameterId of srcParamIds) {
    await saveMidiBinding({ scope, entityId: targetEntityId, parameterId, binding: source[parameterId] });
    step();
  }

  // Clear phase — remove only the target's leftovers that the source didn't overwrite.
  for (const parameterId of leftovers) {
    await clearMidiBinding({ scope, entityId: targetEntityId, parameterId });
    step();
  }
}
